/**
 * Guided Workflow Tooltips - Context-aware guidance for VRP workflow
 * Provides step-by-step tooltips and help content for Project → Scenario → Dataset → Data flow
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Tooltip,
  Typography,
  IconButton,
  Popover,
  Card,
  CardContent,
  Stack,
  Chip,
  Button,
  Divider,
  LinearProgress,
  Badge,
  Fade,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Alert,
} from '@mui/material';
import {
  Help as HelpIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncompletedIcon,
  TrendingFlat as ArrowIcon,
  FolderOpen as ProjectIcon,
  ViewInAr as ScenarioIcon,
  Dataset as DatasetIcon,
  Storage as DataIcon,
  PlayArrow as StartIcon,
  Close as CloseIcon,
  Lightbulb as TipIcon,
  Speed as OptimisationIcon,
} from '@mui/icons-material';
import { useVRPData } from '../../contexts/VRPDataContext';

interface GuidedWorkflowTooltipsProps {
  context?: 'projects' | 'scenarios' | 'datasets' | 'data-management' | 'optimization';
  showProgress?: boolean;
  onSuggestAction?: (action: string, data?: any) => void;
}

interface WorkflowStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  completed: boolean;
  current: boolean;
  actionLabel?: string;
  actionCallback?: () => void;
  tips?: string[];
}

const WORKFLOW_EXPLANATIONS = {
  project: {
    title: 'Project',
    description: 'A project is the top-level container for all your VRP work. It organizes related scenarios and their data.',
    examples: ['Delivery Routes 2024', 'Emergency Services Coverage', 'Supply Chain Optimization'],
    tips: [
      'Use descriptive project names',
      'One project per business problem',
      'Projects can contain multiple scenarios for comparison'
    ]
  },
  scenario: {
    title: 'Scenario',
    description: 'A scenario represents a specific configuration or "what-if" situation within your project.',
    examples: ['Current Routes', 'Peak Season', 'Fleet Expansion', 'Cost Reduction'],
    tips: [
      'Create scenarios to compare different approaches',
      'Each scenario has its own independent data',
      'Use descriptive names that explain the scenario purpose'
    ]
  },
  dataset: {
    title: 'Dataset',
    description: 'A dataset contains the actual VRP data (locations, vehicles, jobs) for a specific scenario.',
    examples: ['Q1 Data', 'Historical Routes', 'Test Configuration'],
    tips: [
      'Datasets can be copied between scenarios',
      'Each dataset maintains data integrity',
      'Use versioned names for data tracking'
    ]
  },
  data: {
    title: 'VRP Data',
    description: 'The actual entities that the optimisation engine needs: locations (depots/customers), vehicles (fleet), and jobs (tasks).',
    examples: ['Warehouse locations', 'Delivery trucks', 'Customer orders'],
    tips: [
      'All data must have valid coordinates',
      'Vehicles need capacity and start locations',
      'Jobs require service times and locations'
    ]
  }
};

const theme = {
  primary: '#1976d2',
  secondary: '#14B8A6',
  accent: '#f59e0b',
  success: '#16a34a',
  warning: '#f59e0b',
  error: '#dc2626',
  gradients: {
    primary: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    secondary: 'linear-gradient(135deg, #14B8A6 0%, #0f766e 100%)',
  },
};

export const GuidedWorkflowTooltips: React.FC<GuidedWorkflowTooltipsProps> = ({
  context = 'data-management',
  showProgress = true,
  onSuggestAction
}) => {
  // TEMPORARILY DISABLED - Will be updated in Phase 3 with proper Toolpad navigation
  return null;
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  const currentScenario = null; // Add missing variable
  const { currentDataset, datasets } = useVRPData();
  
  const [helpAnchorEl, setHelpAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [showWorkflowGuide, setShowWorkflowGuide] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Calculate workflow completion status
  const workflowSteps: WorkflowStep[] = [
    {
      id: 'project',
      title: 'Select Project',
      description: 'Choose or create a project to organize your work',
      icon: ProjectIcon,
      completed: !!currentProject,
      current: !currentProject,
      actionLabel: currentProject ? undefined : 'Create Project',
      actionCallback: () => onSuggestAction?.('create-project'),
      tips: [
        'Projects group related optimization work',
        'Use descriptive names like "Delivery Routes 2024"',
        'One project per business problem'
      ]
    },
    {
      id: 'scenario',
      title: 'Create Scenario',
      description: 'Set up a scenario to represent a specific configuration',
      icon: ScenarioIcon,
      completed: !!currentScenario,
      current: !!currentProject && !currentScenario,
      actionLabel: currentScenario ? undefined : 'Create Scenario',
      actionCallback: () => onSuggestAction?.('create-scenario'),
      tips: [
        'Scenarios enable "what-if" analysis',
        'Examples: "Current State", "Peak Season", "Fleet Expansion"',
        'Each scenario has independent data'
      ]
    },
    {
      id: 'dataset',
      title: 'Create Dataset',
      description: 'Set up a dataset to contain your VRP data',
      icon: DatasetIcon,
      completed: !!currentDataset,
      current: !!currentScenario && !currentDataset,
      actionLabel: currentDataset ? undefined : 'Create Dataset',
      actionCallback: () => onSuggestAction?.('create-dataset'),
      tips: [
        'Datasets organize locations, vehicles, and jobs',
        'Can be copied between scenarios for comparison',
        'Use versioned names for tracking changes'
      ]
    },
    {
      id: 'data',
      title: 'Add VRP Data',
      description: 'Import or create locations, vehicles, and jobs',
      icon: DataIcon,
      completed: false, // Would need actual data counts to determine
      current: !!currentDataset,
      actionLabel: 'Import Data',
      actionCallback: () => onSuggestAction?.('import-data'),
      tips: [
        'Start with locations (depots and customers)',
        'Add vehicles with capacity and start locations',
        'Create jobs with service times and requirements'
      ]
    }
  ];

  // Find current active step
  useEffect(() => {
    const currentStepIndex = workflowSteps.findIndex(step => step.current);
    if (currentStepIndex !== -1) {
      setActiveStep(currentStepIndex);
    }
  }, [currentProject, currentScenario, currentDataset]);

  const handleHelpClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setHelpAnchorEl(event.currentTarget);
  };

  const handleHelpClose = () => {
    setHelpAnchorEl(null);
  };

  const getContextualHelp = () => {
    switch (context) {
      case 'projects':
        return WORKFLOW_EXPLANATIONS.project;
      case 'scenarios':
        return WORKFLOW_EXPLANATIONS.scenario;
      case 'datasets':
        return WORKFLOW_EXPLANATIONS.dataset;
      case 'data-management':
        return WORKFLOW_EXPLANATIONS.data;
      default:
        return WORKFLOW_EXPLANATIONS.project;
    }
  };

  const contextHelp = getContextualHelp();
  const helpOpen = Boolean(helpAnchorEl);

  const getNextAction = () => {
    const nextStep = workflowSteps.find(step => step.current && !step.completed);
    return nextStep;
  };

  const nextAction = getNextAction();

  return (
    <Box>
      {/* Help Button */}
      <Tooltip title="VRP Workflow Guide">
        <IconButton
          onClick={handleHelpClick}
          size="small"
          sx={{
            color: theme.primary,
            backgroundColor: `${theme.primary}10`,
            '&:hover': {
              backgroundColor: `${theme.primary}20`,
            }
          }}
        >
          <Badge 
            color="warning" 
            variant="dot" 
            invisible={!nextAction}
            sx={{
              '& .MuiBadge-badge': {
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.2)', opacity: 0.7 },
                  '100%': { transform: 'scale(1)', opacity: 1 },
                }
              }
            }}
          >
            <HelpIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Workflow Progress (optional) */}
      {showProgress && (
        <Box sx={{ ml: 1, minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Workflow Progress
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(workflowSteps.filter(s => s.completed).length / workflowSteps.length) * 100}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(0,0,0,0.1)',
              '& .MuiLinearProgress-bar': {
                background: theme.gradients.primary,
                borderRadius: 3,
              }
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {workflowSteps.filter(s => s.completed).length} of {workflowSteps.length} steps complete
          </Typography>
        </Box>
      )}

      {/* Help Popover */}
      <Popover
        open={helpOpen}
        anchorEl={helpAnchorEl}
        onClose={handleHelpClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: 480,
            borderRadius: 3,
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          }
        }}
      >
        <Card elevation={0}>
          <Box sx={{
            background: theme.gradients.primary,
            color: 'white',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TipIcon />
              <Typography variant="h6" fontWeight="600">
                VRP Workflow Guide
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleHelpClose}
              sx={{ color: 'white' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <CardContent sx={{ p: 3 }}>
            {/* Next Action Alert */}
            {nextAction && (
              <Alert
                severity="info"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      nextAction.actionCallback?.();
                      handleHelpClose();
                    }}
                  >
                    {nextAction.actionLabel}
                  </Button>
                }
                sx={{ mb: 3 }}
              >
                <Typography variant="body2" fontWeight="500">
                  Next: {nextAction.title}
                </Typography>
                <Typography variant="caption">
                  {nextAction.description}
                </Typography>
              </Alert>
            )}

            {/* Workflow Steps */}
            <Stepper activeStep={activeStep} orientation="vertical">
              {workflowSteps.map((step, index) => (
                <Step key={step.id}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: step.completed ? theme.success : 
                                         step.current ? theme.primary : 'rgba(0,0,0,0.12)',
                          color: 'white',
                          fontSize: '0.75rem'
                        }}
                      >
                        {step.completed ? (
                          <CheckCircleIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <step.icon sx={{ fontSize: 14 }} />
                        )}
                      </Box>
                    )}
                  >
                    <Typography variant="body2" fontWeight="600">
                      {step.title}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {step.description}
                    </Typography>
                    {step.tips && (
                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                        {step.tips.map((tip, tipIndex) => (
                          <Typography
                            key={tipIndex}
                            variant="caption"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: 'text.secondary'
                            }}
                          >
                            <Box
                              sx={{
                                width: 4,
                                height: 4,
                                borderRadius: '50%',
                                backgroundColor: theme.accent,
                                flexShrink: 0
                              }}
                            />
                            {tip}
                          </Typography>
                        ))}
                      </Stack>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>

            <Divider sx={{ my: 3 }} />

            {/* Current Context Help */}
            <Box>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                About {contextHelp.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {contextHelp.description}
              </Typography>
              
              {contextHelp.examples && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" fontWeight="500" color="text.secondary">
                    Examples:
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    {contextHelp.examples.map((example, index) => (
                      <Chip
                        key={index}
                        label={example}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Quick Actions */}
            <Box>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                Quick Actions
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  startIcon={<StartIcon />}
                  onClick={() => setShowWorkflowGuide(true)}
                >
                  Full Guide
                </Button>
                <Button
                  size="small"
                  startIcon={<OptimisationIcon />}
                  onClick={() => onSuggestAction?.('optimisation-compatibility')}
                >
                  Optimisation Check
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Popover>
    </Box>
  );
};

export default GuidedWorkflowTooltips;